import { concat, flatten, last, map, pathOr, reject } from 'ramda'

import { updateContentVariableUsage } from 'api/playbooks'

import { isEmptyOrNil } from '.'

const jinjaRegex = /(?<={{contentVariable.)(.*?)(?=}})/g

export const parseContentVar = (messageString, answerId) => {
  const splitMessage = messageString?.match(jinjaRegex)
  if (isEmptyOrNil(splitMessage)) {
    return []
  }
  return reject(
    isEmptyOrNil,
    splitMessage.map((contentVarString) => {
      const contentVarDetails = contentVarString.split('.')
      let currentVar = {
        contentVariableId: parseInt(contentVarDetails[0].replace('_', '')),
        id: last(contentVarDetails),
      }
      if (!isEmptyOrNil(answerId)) {
        currentVar.outputId = answerId
        currentVar.instanceType = 'RESPONSE'
      }
      return currentVar
    })
  )
}

const generateCurrentVariables = ({
  text,
  instanceType,
  defaultVariableObject,
  currentVariables,
}) => {
  return concat(
    map(
      (variable) => ({ ...variable, ...defaultVariableObject, instanceType }),
      parseContentVar(text)
    ),
    currentVariables
  )
}

export const registerContentVariables = async (
  node,
  playbook,
  isExperiment = false
) => {
  if (isEmptyOrNil(node) || isEmptyOrNil(playbook)) {
    return null
  }
  const { configuration = {} } = node
  const {
    answers = [],
    prompt = '',
    statement = '',
    endOfConversationConfig = {},
    defaultResponse = {},
    postHookErrorMessageTemplate = '',
  } = configuration
  const isBlock = !isEmptyOrNil(pathOr(null, ['meta', 'blockId'], playbook))

  const defaultVariableObject = {
    sourceId: isBlock ? playbook.meta.blockId : playbook.id,
    sourceType: isBlock ? 'BLOCK' : 'PLAYBOOK',
    nodeId: node.id,
    meta: {
      isExperiment: isExperiment,
    },
  }
  let currentVariables = []

  if (!isEmptyOrNil(postHookErrorMessageTemplate)) {
    currentVariables = [
      ...currentVariables,
      ...generateCurrentVariables({
        text: postHookErrorMessageTemplate,
        defaultVariableObject,
        currentVariables,
        instanceType: 'MESSAGE_TEXT',
      }),
    ]
  }

  if (!isEmptyOrNil(endOfConversationConfig)) {
    currentVariables = [
      ...currentVariables,
      ...generateCurrentVariables({
        text: endOfConversationConfig.messageText,
        defaultVariableObject,
        currentVariables,
        instanceType: 'MESSAGE_TEXT',
      }),
    ]
  }

  if (!isEmptyOrNil(answers)) {
    //answers are special cased as they have different data structures
    const variablesFromAnswers = reject(
      isEmptyOrNil,
      flatten(
        map((answer) => parseContentVar(answer.response, answer.id), answers)
      )
    )
    currentVariables = [
      ...currentVariables,
      ...variablesFromAnswers.map((variable) => ({
        ...variable,
        ...defaultVariableObject,
        instanceType: 'RESPONSE',
      })),
    ]
  }

  if (!isEmptyOrNil(prompt)) {
    currentVariables = [
      ...currentVariables,
      ...generateCurrentVariables({
        text: prompt,
        defaultVariableObject,
        currentVariables,
        instanceType: 'PROMPT',
      }),
    ]
  }

  if (!isEmptyOrNil(statement)) {
    currentVariables = [
      ...currentVariables,
      ...generateCurrentVariables({
        text: statement,
        defaultVariableObject,
        currentVariables,
        instanceType: 'STATEMENT',
      }),
    ]
  }

  if (
    !isEmptyOrNil(defaultResponse) &&
    !isEmptyOrNil(defaultResponse.response)
  ) {
    currentVariables = [
      ...currentVariables,
      ...generateCurrentVariables({
        text: defaultResponse.response,
        defaultVariableObject,
        currentVariables,
        instanceType: 'MESSAGE_TEXT',
      }),
    ]
  }

  try {
    await updateContentVariableUsage(currentVariables)
  } catch (e) {
    console.warn('Error encountered registering variables', currentVariables)
  }
}
